import { CommandType, isBranchRequired } from '~/constants/commands'
import { RepoContext } from '~/hooks/task/useRepoContexts'
import { getRepoSourceType, renderRepoContext } from '~/utils/repositoryUtils'

interface CodeContextHeaderProps {
  repoContexts: RepoContext[]
  commandType?: CommandType
}

/**
 * Displays repository and branch information from repo contexts.
 * Handles uploaded projects, local sync, and Git repos (single and multi-repo).
 *
 * Format:
 * - Git repos: org/repo (branch)
 * - Uploaded projects: project-name (uploaded)
 * - Local sync: project-name (local)
 * - Multi-repo: repo1 (branch1), repo2 (uploaded), repo3 (local)
 */
export function CodeContextHeader({ repoContexts, commandType }: CodeContextHeaderProps) {
  if (repoContexts.length === 0) {
    return null
  }

  // For multi-repo or mixed scenarios, show each repo inline with its branch/status
  const repositoryDisplay = repoContexts
    .map(ctx => {
      const sourceType = getRepoSourceType(ctx)

      // For git repos, only show branch if command requires it
      if (sourceType === 'git') {
        const showBranch = !commandType || isBranchRequired(commandType)
        return renderRepoContext(ctx, { showBranch })
      }

      // For uploaded and local repos, use default rendering
      return renderRepoContext(ctx)
    })
    .filter(Boolean)
    .join(', ')

  return (
    <>
      {repositoryDisplay && (
        <div className='label flex items-center select-none mx-[10px]'>
          <div className='label-lg-bold pr-1'>Repository: </div>
          <div className='label-lg-normal'>{repositoryDisplay}</div>
        </div>
      )}
    </>
  )
}
